import type { BoardGroupBy, SortBy, SortDirection } from '@/store/ProjectViewPreferencesStore'

// Minimum subset of task fields needed for sorting / grouping.
export interface SortableTask {
  id: string
  name: string
  priority?: number | null
  dueDate?: number | null
  createdOn: number
  sortOrder?: string | null
  statusId?: string | null
  userId?: string | null
}

function lexoCompare(a?: string | null, b?: string | null): number {
  if (a && b) return a.localeCompare(b)
  if (a) return -1
  if (b) return 1
  return 0
}

// Priority: 0 = no priority (treat as lowest), 1 = P1 (highest), 2 = P2, ...
function priorityRank(p: number | null | undefined): number {
  if (p == null || p <= 0) return Number.POSITIVE_INFINITY
  return p
}

export function sortTasks<T extends SortableTask>(
  tasks: Array<T>,
  sortBy: SortBy,
  sortDirection: SortDirection = 'asc',
): Array<T> {
  // Manual ordering has no asc/desc semantics — always use the raw sortOrder.
  if (sortBy === 'manual') {
    return [...tasks].sort((a, b) => {
      const cmp = lexoCompare(a.sortOrder, b.sortOrder)
      if (cmp !== 0) return cmp
      return a.createdOn - b.createdOn
    })
  }

  // The existing comparators were written in their "natural" ascending form
  // (P1 first, earliest due date first, oldest created first in raw terms,
  // A→Z name). createdDate is special: the old hard-coded default was
  // newest-first, which we now express as the caller passing 'desc'.
  // So all comparators here produce the *ascending* ordering, and we flip
  // the sign for desc.
  const dir = sortDirection === 'desc' ? -1 : 1

  // dueDate: nulls always last, regardless of direction.
  if (sortBy === 'dueDate') {
    return [...tasks].sort((a, b) => {
      const aNull = a.dueDate == null
      const bNull = b.dueDate == null
      if (aNull && bNull) return lexoCompare(a.sortOrder, b.sortOrder)
      if (aNull) return 1
      if (bNull) return -1
      if (a.dueDate !== b.dueDate) return dir * ((a.dueDate as number) - (b.dueDate as number))
      return lexoCompare(a.sortOrder, b.sortOrder)
    })
  }

  // priority: no-priority tasks always last (parallel to dueDate-null handling).
  if (sortBy === 'priority') {
    return [...tasks].sort((a, b) => {
      const ra = priorityRank(a.priority)
      const rb = priorityRank(b.priority)
      const aNone = !isFinite(ra)
      const bNone = !isFinite(rb)
      if (aNone && bNone) return lexoCompare(a.sortOrder, b.sortOrder)
      if (aNone) return 1
      if (bNone) return -1
      if (ra !== rb) return dir * (ra - rb)
      return lexoCompare(a.sortOrder, b.sortOrder)
    })
  }

  return [...tasks].sort((a, b) => {
    let primary = 0
    switch (sortBy) {
      case 'createdDate': {
        primary = a.createdOn - b.createdOn
        break
      }
      case 'name': {
        primary = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        break
      }
    }
    if (primary !== 0) return dir * primary
    return lexoCompare(a.sortOrder, b.sortOrder)
  })
}

export function getGroupKeyForTask(task: SortableTask, groupBy: BoardGroupBy): string {
  switch (groupBy) {
    case 'status':
      return task.statusId ?? ''
    case 'priority':
      return String(task.priority ?? 0)
    case 'assignee':
      return task.userId || '__unassigned__'
  }
}
